import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function DoubleParameterStep({ sessionNumber, intro, onDone }) {
  const [d, setD] = useState({ negative: "", friend: "", reframed: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!d.negative.trim() || !d.friend.trim() || !d.reframed.trim()) {
      toast.error("Completa los tres campos.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/progress", { session_number: sessionNumber, step: "double-parameter", completed: true, payload: d });
      toast.success("Ejercicio guardado");
      onDone(d);
    } catch {
      toast.error("No pudimos guardar el ejercicio.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-[var(--ha-muted)] leading-relaxed max-w-2xl">{intro}</p>

      <div className="mt-6 rounded-2xl bg-[var(--ha-secondary)]/10 border border-[var(--ha-secondary)]/25 p-5 flex items-start gap-3">
        <HeartHandshake className="w-5 h-5 text-[var(--ha-secondary)] mt-0.5" strokeWidth={1.75} />
        <p className="text-sm text-[var(--ha-text)] leading-relaxed">Cuando te trates con dureza, pregúntate: <em>¿le hablaría así a un amigo querido con este mismo problema?</em> La respuesta suele ser no. Trátate con esa misma amabilidad.</p>
      </div>

      <div className="mt-6 space-y-4">
        <Card className="rounded-2xl border-[var(--ha-border)] bg-white">
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="text-sm font-semibold text-[var(--ha-text)]">1. Tu pensamiento negativo</label>
              <Textarea data-testid="dp-negative" value={d.negative} onChange={(e) => setD({ ...d, negative: e.target.value })} placeholder="Escribe el pensamiento negativo o autocrítico" className="mt-2 rounded-xl border-[var(--ha-border)]" />
            </div>
            <div>
              <label className="text-sm font-semibold text-[var(--ha-text)]">2. ¿Cómo le hablarías a un amigo?</label>
              <Textarea data-testid="dp-friend" value={d.friend} onChange={(e) => setD({ ...d, friend: e.target.value })} placeholder="Escribe lo que le dirías a un amigo con este mismo pensamiento" className="mt-2 rounded-xl border-[var(--ha-border)]" />
            </div>
            <div>
              <label className="text-sm font-semibold text-[var(--ha-text)]">3. Reformula el pensamiento</label>
              <Textarea data-testid="dp-reframed" value={d.reframed} onChange={(e) => setD({ ...d, reframed: e.target.value })} placeholder="Ahora, escribe la versión reformulada para ti mismo/a" className="mt-2 rounded-xl border-[var(--ha-border)]" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8 flex justify-end">
        <Button data-testid="dp-continue" onClick={save} disabled={saving} className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6">
          Guardar y continuar <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
